import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, ArrowLeft, ArrowRight, Check, Sparkles, Download, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import Step1JobDescription from '../components/wizard/Step1JobDescription';
import Step2ResumeUpload from '../components/wizard/Step2ResumeUpload';
import Step3Processing from '../components/wizard/Step3Processing';
import Step4ReviewResume from '../components/wizard/Step4ReviewResume';
import Step5CoverLetter from '../components/wizard/Step5CoverLetter';
import Step6Download from '../components/wizard/Step6Download';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Wizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    jobDescription: '',
    resumeFile: null,
    resumeText: '',
    analysis: null,
    tailoredResume: '',
    toolsToValidate: [],
    coverLetter: '',
  });

  // Load saved resume from localStorage on mount
  useEffect(() => {
    const savedResume = localStorage.getItem('dashworx_saved_resume');
    const savedResumeFilename = localStorage.getItem('dashworx_saved_resume_filename');
    
    if (savedResume) {
      console.log('Found saved resume, auto-loading...');
      setWizardData(prev => ({
        ...prev,
        resumeText: savedResume,
        resumeFile: savedResumeFilename ? { name: savedResumeFilename } : null
      }));
    }
  }, []);

  const steps = [
    { number: 1, title: 'Job Description', component: Step1JobDescription },
    { number: 2, title: 'Upload Resume', component: Step2ResumeUpload },
    { number: 3, title: 'Processing', component: Step3Processing },
    { number: 4, title: 'Review Resume', component: Step4ReviewResume },
    { number: 5, title: 'Cover Letter', component: Step5CoverLetter },
    { number: 6, title: 'Download', component: Step6Download },
  ];

  const updateWizardData = (data) => {
    setWizardData(prev => ({ ...prev, ...data }));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div className="min-h-screen flex flex-col lg:grid lg:grid-cols-12">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl font-bold tracking-tight">Dashworx</h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-semibold text-primary">Step {currentStep}</span>
            <span>/</span>
            <span>{steps.length}</span>
          </div>
        </div>
        
        {/* Mobile Progress Bar */}
        <div className="mt-3 h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:col-span-4 bg-slate-50 dark:bg-slate-900 p-8 border-r border-slate-200 dark:border-slate-800 flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-12">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Dashworx</h1>
          </div>

          <div className="space-y-4">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`flex items-center gap-4 p-4 rounded-lg transition-all ${
                  step.number === currentStep
                    ? 'bg-primary text-white shadow-lg'
                    : step.number < currentStep
                    ? 'bg-white dark:bg-slate-800 text-primary'
                    : 'bg-white/50 dark:bg-slate-800/50 text-muted-foreground'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                    step.number === currentStep
                      ? 'bg-white text-primary'
                      : step.number < currentStep
                      ? 'bg-primary text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                  }`}
                >
                  {step.number < currentStep ? <Check className="w-4 h-4" /> : step.number}
                </div>
                <div>
                  <p className="text-sm font-medium">{step.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <Button
            data-testid="back-to-home-btn"
            variant="ghost"
            className="w-full justify-start"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:col-span-8 p-4 sm:p-8 lg:p-16 flex flex-col max-w-3xl mx-auto w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <CurrentStepComponent
              wizardData={wizardData}
              updateWizardData={updateWizardData}
              nextStep={nextStep}
              prevStep={prevStep}
              currentStep={currentStep}
            />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default Wizard;
